import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { setAuth, setPropertyConfig } from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2 } from 'lucide-react';

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [propertyId, setPropertyId] = useState('royal_tulip');
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const res = await api.get('/properties');
        setProperties(res.data);
      } catch {
        setProperties([
          { id: 'royal_tulip', name: 'Royal Tulip Batu', short_name: 'RTS' },
          { id: 'golden_tulip', name: 'Golden Tulip Holland Resort Batu', short_name: 'GTHR' }
        ]);
      }
    };
    fetchProperties();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', { username, password, property_id: propertyId });
      const { access_token, user } = response.data;
      
      setAuth(access_token, user, propertyId);
      
      // Fetch property config
      try {
        const configRes = await api.get('/property/config', {
          headers: { Authorization: `Bearer ${access_token}` }
        });
        setPropertyConfig(configRes.data);
      } catch {}
      
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const selectedProp = properties.find(p => p.id === propertyId);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Top/Left side - Image */}
      <div className="h-48 lg:h-auto lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_royal-inventory-4/artifacts/mg29ahke_79aaee2e-fd1f-42d6-8280-762ab3c2e355.jpeg)'
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 lg:p-12">
          <h1 className="text-2xl lg:text-4xl font-bold text-white mb-1 lg:mb-4">
            {selectedProp?.short_name || 'RTS'} Inventory
          </h1>
          <p className="text-sm lg:text-lg text-white/90">
            {selectedProp?.name || 'Stock Inventory Management System'}
          </p>
        </div>
      </div>

      {/* Bottom/Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-8 bg-white">
        <div className="w-full max-w-md space-y-6 lg:space-y-8">
          <div className="text-center">
            <h2 className="text-2xl lg:text-3xl font-bold mb-2" style={{color: '#C84C32'}} data-testid="login-heading">Welcome Back</h2>
            <p className="text-sm lg:text-base text-muted-foreground">Sign in to access your inventory</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4 lg:space-y-6" data-testid="login-form">
            {/* Property Selector */}
            <div className="space-y-2">
              <Label htmlFor="property">Property</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <select
                  id="property"
                  data-testid="property-select"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="username-input"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Enter your username"
                className="input-focus"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="input-focus"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full btn-primary"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Copyright &copy; 2025 created by <span className="font-semibold text-primary">luckytech</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
